// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useState } from 'react';
import { CallProvider } from '@azure/communication-react';
import { Stack } from '@fluentui/react';
import { useEffect, useRef } from 'react';
import * as styles from '../styles/OpenAiWidget.styles';
import { Call } from '@azure/communication-calling';
import { OpenAiControlBar } from './OpenAiControlBar';
import WaveAnimation from './WaveAnimation';
import InerviewStepper from './InterviewStepper';

type AgentMessage = {
  id: string;
  text: string;
  timestamp: string;
};

interface OpenAiWidgetCallScreenProps {
  currentCall: Call;
  onHangup: () => void;
  messages?: AgentMessage[];
}

export const OpenAiWidgetCallScreen = ({
  currentCall,
  onHangup,
  messages = []
}: OpenAiWidgetCallScreenProps): JSX.Element => {
  // const currentPulsatingLogoWrapperStyle = { ...styles.cardPulsatingLogoWrapperStyle, animation: 'pulse 2s infinite' };
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (messages.length > 0) {
      setCurrentIndex(messages.length - 1);
    }
  }, [messages]);

  return (
    <CallProvider call={currentCall}>
      <Stack style={styles.agentSpeakingCardStyle}>
        <div style={styles.waveContainerStyle}>
          <WaveAnimation />
        </div>
        <div style={styles.stepperContainerStyle}>
          <InerviewStepper />
        </div>

        <div style={styles.messageWrapperStyle}>
          <div ref={messageListRef} style={styles.messageScrollContainerStyle}>
            {messages.length === 0 ? (
              <p style={{ margin: 0, color: '#605e5c' }}>Waiting for assistant response…</p>
            ) : (
              <Stack
                key={messages[currentIndex].id}
                style={styles.slideInRightStyle}
                styles={{
                  root: {
                    animation: 'slideInRight 0.4s ease',
                    backgroundColor: 'transparent',
                    padding: 12,
                    width: '100%',
                    maxWidth: '500px'
                  }
                }}
              >
                <p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '22px', color: '#004140' }}>AI</p>

                <p style={{ margin: 0, fontSize: '18px', color: '#004140' }}>{messages[currentIndex].text}</p>

                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#00414080' }}>
                  {new Date(messages[currentIndex].timestamp).toLocaleTimeString()}
                </p>
              </Stack>
            )}
          </div>
        </div>

        {/* CONTROL BAR */}
        <OpenAiControlBar onHangup={onHangup} />
      </Stack>
    </CallProvider>
  );
};
